import React, { useState, useEffect } from "react";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import {
  API_ops_assistant_confirm_action,
  API_get_role_names,
} from "../../../constants";
import { toast } from "react-toastify";

const PERMISSION_OPTIONS = [
  { value: "company_employee", label: "Employee" },
  { value: "company_manager", label: "Manager" },
  { value: "company_admin", label: "Admin", adminOnly: true },
  { value: "company_timeclock", label: "Timeclock" },
];

// Only company_admin and rightwork_internal can invite at the
// company_admin permission level. Mirrors the backend gate in the
// orchestration resolution and the `_user_is_company_admin_at_store`
// check inside the executor — keeping the option visible to non-admins
// would let them pick it, hit the backend error, and feel stuck.
const ADMIN_ROLES = new Set(["company_admin", "rightwork_internal"]);

const WELCOME_CHANNEL_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

// Lightweight email shape check — backend re-validates with Django's
// validate_email so this is just a UX guard against obvious typos.
const EMAIL_LIKE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  permissionRole,
  onStatusChange,
  confirmUrl = API_ops_assistant_confirm_action,
  scopeParams,
}) {
  const callerIsAdmin = ADMIN_ROLES.has(permissionRole);
  const visiblePermissionOptions = callerIsAdmin
    ? PERMISSION_OPTIONS
    : PERMISSION_OPTIONS.filter((opt) => !opt.adminOnly);
  const summary = proposal.summary || {};
  const initialRoles = Array.isArray(summary.resolved_roles)
    ? summary.resolved_roles.map((r) => ({
        role_id: r.role_id,
        role_name: r.role_name || "",
        is_primary: !!r.is_primary,
        proficiency_rating: r.proficiency_rating ?? 3,
      }))
    : [];

  const initialFirstName = (() => {
    const name = (summary.employee_name || "").trim();
    if (!name) return "";
    return name.split(/\s+/)[0] || "";
  })();
  const initialLastName = (() => {
    const name = (summary.employee_name || "").trim();
    if (!name) return "";
    const parts = name.split(/\s+/);
    return parts.slice(1).join(" ");
  })();

  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);

  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [email, setEmail] = useState(summary.email || "");
  const [phone, setPhone] = useState(summary.phone || "");
  // If the LLM somehow proposed company_admin to a non-admin caller,
  // downshift the initial value so the dropdown isn't empty/invalid
  // and the user can re-pick. The backend would reject the admin
  // proposal at execute time anyway.
  const initialPermission = (() => {
    const proposed = summary.permission_type || "company_employee";
    if (!callerIsAdmin && proposed === "company_admin") {
      return "company_employee";
    }
    return proposed;
  })();
  const [permissionType, setPermissionType] = useState(initialPermission);
  const [sendWelcome, setSendWelcome] = useState(
    summary.send_welcome !== undefined ? !!summary.send_welcome : true
  );
  const [welcomeChannel, setWelcomeChannel] = useState(
    summary.welcome_channel || "email"
  );
  const [roles, setRoles] = useState(initialRoles);

  // Available store roles for the role picker
  const [storeRoles, setStoreRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  useEffect(() => {
    let cancelled = false;
    const axiosConfig = getDefaultAxiosConfig(homeStore);
    axios
      .get(`${API_get_role_names}${homeStore}`, axiosConfig)
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        // The endpoint returns full role objects; we just need {id, role_name}.
        setStoreRoles(
          list
            .filter((r) => r && r.id && r.role_name)
            .map((r) => ({ id: r.id, role_name: r.role_name }))
        );
        setRolesLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setRolesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [homeStore]);

  const updateRole = (idx, patch) => {
    setRoles((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r))
    );
  };

  const setPrimaryRole = (idx) => {
    setRoles((prev) =>
      prev.map((r, i) => ({ ...r, is_primary: i === idx }))
    );
  };

  const removeRole = (idx) => {
    setRoles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // Ensure at least one primary remains
      if (next.length > 0 && !next.some((r) => r.is_primary)) {
        next[0].is_primary = true;
      }
      return next;
    });
  };

  const addRole = () => {
    if (!storeRoles.length) return;
    // Pick the first store role not already in the list
    const existingIds = new Set(roles.map((r) => r.role_id));
    const next = storeRoles.find((r) => !existingIds.has(r.id));
    if (!next) return;
    setRoles((prev) => {
      const isFirst = prev.length === 0;
      return [
        ...prev,
        {
          role_id: next.id,
          role_name: next.role_name,
          is_primary: isFirst,
          proficiency_rating: 3,
        },
      ];
    });
  };

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        ...(scopeParams || { store_id: homeStore }),
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };

      if (action === "confirm") {
        // Send the full edited form back as overrides. confirmation.py
        // merges these into tool_args before the executor runs.
        const overrides = {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          phone_number: phone.trim(),
          permission_type: permissionType,
          send_welcome: sendWelcome,
          welcome_channel: welcomeChannel,
          roles: roles.map((r) => ({
            role_id: r.role_id,
            role_name: r.role_name,
            is_primary: !!r.is_primary,
            // Proficiency isn't shown in this form. New hires land at
            // the default and managers tune it later in the Roles UI —
            // this keeps the card predictable and avoids the LLM's
            // arbitrary guess sneaking in unseen.
            proficiency_rating: 3,
          })),
        };
        payload.overrides = overrides;
      }

      const res = await postRightworkAPI(confirmUrl, payload);
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to add employee.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.system?.message ||
          err?.response?.data?.error ||
          "Failed to add employee."
      );
    }
    setLoading(false);
  };

  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon
          fontSize="small"
          className="text-green-600 mt-0.5 shrink-0"
        />
        <div className="min-w-0">
          <div className="font-medium">
            Added {firstName} {lastName}
          </div>
          <div className="text-xs opacity-80 truncate">
            {email}
            {sendWelcome && ` · welcome sent via ${welcomeChannel}`}
          </div>
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Cancelled</span>
      </div>
    );
  }

  // ----- Validation -----
  const trimmedEmail = email.trim();
  const emailLooksValid = EMAIL_LIKE_RE.test(trimmedEmail);
  const primaryCount = roles.filter((r) => r.is_primary).length;
  const smsWithoutPhone =
    sendWelcome && welcomeChannel === "sms" && !phone.trim();
  const confirmDisabled =
    loading ||
    !firstName.trim() ||
    !lastName.trim() ||
    !trimmedEmail ||
    !emailLooksValid ||
    !permissionType ||
    roles.length === 0 ||
    primaryCount !== 1 ||
    smsWithoutPhone;

  // For the role picker dropdown, exclude roles already used in OTHER rows.
  const roleOptionsFor = (idx) => {
    const usedElsewhere = new Set(
      roles.filter((_, i) => i !== idx).map((r) => r.role_id)
    );
    return storeRoles.filter(
      (r) => !usedElsewhere.has(r.id) || r.id === roles[idx]?.role_id
    );
  };

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <PersonAddIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Add Employee
          {summary.store_name && (
            <span className="text-violet-700 font-normal">
              {" — "}
              {summary.store_name}
            </span>
          )}
        </span>
      </div>

      <div className="px-3 py-2 space-y-2">
        {/* Name */}
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              First name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={100}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-0.5">
              Last name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={100}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={150}
            className={`w-full text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white ${
              email && !emailLooksValid ? "border-red-400" : "border-gray-300"
            }`}
            placeholder="name@example.com"
          />
          {email && !emailLooksValid && (
            <div className="text-[11px] text-red-500 mt-0.5">
              Enter a valid email address.
            </div>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Phone {welcomeChannel === "sms" && sendWelcome ? "" : "(optional)"}
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={25}
            className={`w-full text-sm border rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white ${
              smsWithoutPhone ? "border-red-400" : "border-gray-300"
            }`}
            placeholder="(555) 555-5555 or +52..."
          />
          {smsWithoutPhone && (
            <div className="text-[11px] text-red-500 mt-0.5">
              Phone is required to send an SMS welcome.
            </div>
          )}
        </div>

        {/* Permission type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Permission level
          </label>
          <select
            value={permissionType}
            onChange={(e) => setPermissionType(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white"
          >
            {visiblePermissionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Roles */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-0.5">
            Roles
          </label>
          {!rolesLoaded && (
            <div className="text-[11px] text-gray-500">Loading roles…</div>
          )}
          {rolesLoaded && storeRoles.length === 0 && (
            <div className="text-[11px] text-gray-500">
              No roles configured at this store. Add roles before inviting.
            </div>
          )}
          {roles.length === 0 && rolesLoaded && storeRoles.length > 0 && (
            <div className="text-[11px] text-red-500">
              At least one role is required.
            </div>
          )}
          <div className="space-y-1">
            {roles.map((r, idx) => (
              <div
                key={`${r.role_id}-${idx}`}
                className="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-2 py-1"
              >
                <select
                  value={r.role_id}
                  onChange={(e) => {
                    const newId = Number(e.target.value);
                    const match = storeRoles.find((sr) => sr.id === newId);
                    updateRole(idx, {
                      role_id: newId,
                      role_name: match ? match.role_name : r.role_name,
                    });
                  }}
                  className="flex-1 text-sm border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
                >
                  {roleOptionsFor(idx).map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.role_name}
                    </option>
                  ))}
                </select>
                <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                  <input
                    type="radio"
                    name={`primary-${proposal.proposal_id}`}
                    checked={!!r.is_primary}
                    onChange={() => setPrimaryRole(idx)}
                    className="accent-violet-700"
                  />
                  Primary
                </label>
                <button
                  onClick={() => removeRole(idx)}
                  title="Remove role"
                  className="text-gray-400 hover:text-red-500"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
          {rolesLoaded && storeRoles.length > 0 && (
            <button
              onClick={addRole}
              disabled={roles.length >= storeRoles.length}
              className="text-xs text-violet-700 hover:text-violet-900 mt-1 disabled:text-gray-400"
            >
              + Add role
            </button>
          )}
        </div>

        {/* Welcome */}
        <div className="flex items-center gap-3 pt-1">
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={sendWelcome}
              onChange={(e) => setSendWelcome(e.target.checked)}
              className="accent-violet-700"
            />
            Send welcome
          </label>
          {sendWelcome && (
            <select
              value={welcomeChannel}
              onChange={(e) => setWelcomeChannel(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-400 bg-white"
            >
              {WELCOME_CHANNEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={confirmDisabled}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Add to team
        </button>
      </div>
    </div>
  );
}
